import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import type { MasterDataEntityType } from "@/lib/enums";
import { masterDataMappingKeys } from "@/lib/query";

/**
 * TanStack Query hooks for `master_data_mapping`. Mirrors
 * `src/hooks/use-bu-instances.ts`'s pattern. `entityType` is fixed by the
 * active tab (see `MasterDataMappingsLayout`) rather than a user-adjustable
 * filter, so it — like the BU-pair filter — is appended directly onto
 * `buildListParams`'s output under its own query-string key, not the shared
 * grid's generic `status` slot.
 */

export interface MasterDataMapping {
  id: string;
  entityType: MasterDataEntityType;
  sourceBuId: string;
  targetBuId: string;
  sourceRefId: string;
  /** Null on a draft row created by `syncMasterDataMappingDrafts` — "needs target". */
  targetRefId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Resolved server-side via a join; null if the referenced BU is gone. */
  sourceBuName: string | null;
  targetBuName: string | null;
}

interface MasterDataMappingsListResponse {
  data: MasterDataMapping[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface MasterDataMappingInput {
  entityType: MasterDataEntityType;
  sourceBuId: string;
  targetBuId: string;
  sourceRefId: string;
  targetRefId: string;
  isActive: boolean;
}

export interface BuPairFilter {
  sourceBuId?: string;
  targetBuId?: string;
}

export interface MasterDataMappingsFilters extends BuPairFilter {
  entityType: MasterDataEntityType;
}

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

export function useMasterDataMappings(
  query: DataGridQuery,
  pageRequest: PageRequest,
  filters: MasterDataMappingsFilters,
) {
  return useQuery({
    queryKey: [...masterDataMappingKeys.list({ ...query, ...filters }), pageRequest] as const,
    queryFn: async (): Promise<MasterDataMappingsListResponse> => {
      const params = buildListParams(query, pageRequest);
      params.set("entityType", filters.entityType);
      if (filters.sourceBuId) params.set("sourceBuId", filters.sourceBuId);
      if (filters.targetBuId) params.set("targetBuId", filters.targetBuId);

      const res = await fetch(`/api/admin/master-data-mappings?${params}`);
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, "Failed to fetch master data mappings"));
      }
      return res.json();
    },
  });
}

export function useMasterDataMapping(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: masterDataMappingKeys.detail(id),
    queryFn: async (): Promise<MasterDataMapping> => {
      const res = await fetch(`/api/admin/master-data-mappings/${id}`);
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, "Failed to fetch master data mapping"));
      }
      const json = await res.json();
      return json.data;
    },
    enabled: options?.enabled,
  });
}

export function useCreateMasterDataMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: MasterDataMappingInput): Promise<MasterDataMapping> => {
      const res = await fetch("/api/admin/master-data-mappings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, "Failed to create mapping"));
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: masterDataMappingKeys.all });
      toast.success("Mapping created");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to create mapping");
    },
  });
}

export function useUpdateMasterDataMapping(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<MasterDataMappingInput>): Promise<MasterDataMapping> => {
      const res = await fetch(`/api/admin/master-data-mappings/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, "Failed to update mapping"));
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: masterDataMappingKeys.all });
      toast.success("Mapping updated");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update mapping");
    },
  });
}

export function useDeleteMasterDataMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/admin/master-data-mappings/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Delete failed"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: masterDataMappingKeys.all });
      toast.success("Mapping deleted");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to delete mapping");
    },
  });
}
